"use client";

import { useEffect, useState } from "react";
import { Badge, Button, Box, Typography } from "@mui/material";
import { Person, Logout } from "@mui/icons-material";
import AuthService from "src/services/auth.service";
import { useSnackbar } from "notistack";

interface OriginalUser {
  id: string;
  name: string;
  isMasquerading: boolean;
}

interface MasqueradeIndicatorProps {
  isExpanded?: boolean;
}

export function MasqueradeIndicator({ isExpanded = true }: MasqueradeIndicatorProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [originalUser, setOriginalUser] = useState<OriginalUser | null>(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const storedUser = localStorage.getItem("original_user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setOriginalUser(parsedUser);
      } catch (error) {
        console.error("Error parsing original user data:", error);
        enqueueSnackbar("Error loading masquerade data", {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    }
  }, [enqueueSnackbar]);

  // Only render on client-side to avoid hydration issues
  if (!isClient || !originalUser) return null;

  const handleStopMasquerade = async () => {
    try {
      enqueueSnackbar(`Returning to your account...`, {
        variant: 'info',
        autoHideDuration: 2000,
      });
      await AuthService.stopMasquerade();
      enqueueSnackbar(`Successfully returned to ${originalUser.name}'s account`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error("Failed to stop masquerading:", error);
      enqueueSnackbar("Failed to return to your account. Please try logging in again.", {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  // Compact version for collapsed sidebar
  if (!isExpanded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Badge
          color="error"
          sx={{ cursor: 'pointer' }}
          onClick={handleStopMasquerade}
        >
          <Person sx={{ fontSize: 20 }} />
        </Badge>
      </Box>
    );
  }

  // Full version for expanded sidebar
  return (
    <Box sx={{ px: 2, py: 2, borderTop: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge color="error">
            <Person sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="body2">Masquerading</Typography>
          </Badge>
        </Box>
        <Typography variant="caption" color="text.secondary">
          You are viewing as another user. Return to your account ({originalUser.name}).
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 1, fontSize: '0.75rem' }}
          onClick={handleStopMasquerade}
          startIcon={<Logout sx={{ fontSize: 16 }} />}
        >
          Return to Your Account
        </Button>
      </Box>
    </Box>
  );
} 